import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { mesAtualKey, parseMesFiltro, type MesFiltro } from '../utils/filtroMes'

export interface VendasPorMesData {
  mes: string
  mes_numero: number
  mes_key: string
  total_vendas: number
  valor_total: number
  valor_medio: number
}

function parseDataLocal(value: string): Date {
  // Evita shift de timezone em datas YYYY-MM-DD
  const soData = String(value || '').slice(0, 10)
  const [y, m, d] = soData.split('-').map(Number)
  if (y && m && d) return new Date(y, m - 1, d)
  return new Date(value)
}

/**
 * Gráfico de barras: mostra os 12 meses do ano do filtro selecionado.
 * Ex.: filtro "2025-08" → jan..dez/2025
 */
export const useVendasPorMes = (corretorId?: string, mesFiltro: MesFiltro = mesAtualKey()) => {
  const [data, setData] = useState<VendasPorMesData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchVendasPorMes = async () => {
    try {
      setLoading(true)
      setError(null)

      let finalCorretorId = corretorId
      if (corretorId) {
        const { data: corretor, error: corretorError } = await supabase
          .from('corretores')
          .select('id, user_id')
          .eq('user_id', corretorId)
          .single()

        if (!corretorError && corretor) {
          finalCorretorId = corretor.id
        }
      }

      const { year } = parseMesFiltro(mesFiltro)
      const inicioAno = new Date(year, 0, 1)
      const fimAno = new Date(year, 11, 31, 23, 59, 59, 999)

      let vendasQuery = supabase
        .from('vendas')
        .select(`
          id,
          valor_venda,
          data_venda,
          corretor_id
        `)
        .gte('data_venda', inicioAno.toISOString().slice(0, 10))
        .lte('data_venda', fimAno.toISOString().slice(0, 10))
        .order('data_venda', { ascending: true })

      if (finalCorretorId) {
        vendasQuery = vendasQuery.eq('corretor_id', finalCorretorId)
      }

      const { data: vendasData, error: vendasError } = await vendasQuery

      if (vendasError) {
        throw vendasError
      }

      const vendasPorMesMap = new Map<string, {
        total_vendas: number
        valor_total: number
      }>()

      const meses: { mes: string; mes_numero: number; mes_key: string }[] = []
      for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
        const dataMes = new Date(year, monthIndex, 1)
        const mesKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
        const mesNome = dataMes.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
        meses.push({
          mes: mesNome,
          mes_numero: monthIndex + 1,
          mes_key: mesKey,
        })
        vendasPorMesMap.set(mesKey, {
          total_vendas: 0,
          valor_total: 0,
        })
      }

      vendasData?.forEach((venda) => {
        const dataVenda = parseDataLocal(venda.data_venda)
        const mesKey = `${dataVenda.getFullYear()}-${String(dataVenda.getMonth() + 1).padStart(2, '0')}`

        if (vendasPorMesMap.has(mesKey)) {
          const mesData = vendasPorMesMap.get(mesKey)!
          mesData.total_vendas += 1
          mesData.valor_total += venda.valor_venda || 0
        }
      })

      const vendasProcessed: VendasPorMesData[] = meses.map((mes) => {
        const mesData = vendasPorMesMap.get(mes.mes_key)!
        const valorMedio =
          mesData.total_vendas > 0 ? mesData.valor_total / mesData.total_vendas : 0

        return {
          mes: mes.mes,
          mes_numero: mes.mes_numero,
          mes_key: mes.mes_key,
          total_vendas: mesData.total_vendas,
          valor_total: mesData.valor_total,
          valor_medio: valorMedio,
        }
      })

      setData(vendasProcessed)
    } catch (err) {
      console.error('Erro ao buscar vendas por mês:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVendasPorMes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corretorId, mesFiltro])

  return {
    data,
    loading,
    error,
    refetch: fetchVendasPorMes,
  }
}
